import { useState, useEffect } from "react";
import { API_BASE } from "../config/api";

const STEPS = ["Personal Info", "Address", "Academic", "Motivation", "Payment", "Documents", "Review"];

const GENDERS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

// Documents required
const DOCS_CONFIG = [
  { type: "id_copy",        label: "National ID / Passport Copy",  required: true,  accept: "image/*,.pdf", hint: "Clear scan or photo of your ID or passport" },
  { type: "transcript",     label: "Academic Transcript / Results", required: true,  accept: "image/*,.pdf", hint: "Your most recent school results or certificate" },
  { type: "proof_of_address", label: "Proof of Address",           required: true,  accept: "image/*,.pdf", hint: "Utility bill, bank statement or letter (not older than 3 months)" },
  { type: "payment_screenshot", label: "Proof of Payment",         required: true,  accept: "image/*,.pdf", hint: "Screenshot or receipt of your R150 application fee payment" },
  { type: "motivation_letter", label: "Motivation Letter (File)",   required: false, accept: ".pdf,.doc,.docx", hint: "Upload your motivation letter if you have it as a file" },
  { type: "portfolio",      label: "Portfolio (Optional)",         required: false, accept: "image/*,.pdf", hint: "Design sketches, film clips or IT projects — encouraged for Design & Film applicants" },
];

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "",
  date_of_birth: "", gender: "", nationality: "Basotho", id_number: "",
  address_street: "", address_city: "", address_district: "", address_country: "Lesotho",
  program_id: "", intake_year: new Date().getFullYear() + 1,
  edu_level: "",
  highest_qual: "", school_name: "", school_year: "",
  motivation: "",
};

export default function ApplyPage() {
  const [step, setStep]         = useState(0);
  const [form, setForm]         = useState(EMPTY);
  const [programs, setPrograms] = useState([]);
  const [errors, setErrors]     = useState({});
  const [loading, setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refId, setRefId]       = useState(null);
  const [apiError, setApiError] = useState(null);

  // documents state: { doc_type -> File }
  const [docs, setDocs]         = useState({});
  const [docErrors, setDocErrors] = useState({});
  const [docUploading, setDocUploading] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/programs`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        setPrograms(Array.isArray(data) ? data : []);
        setApiError(null);
      })
      .catch(err => {
        setApiError(err.message);
        setPrograms([]);
      });
  }, []);

  useEffect(() => {
    if (!form.program_id) return;

    const program = programs.find((p) => String(p.id) === String(form.program_id));
    if (!program) return;

    const minimumLevel = program.min_edu_level || "standard7";
    setForm((current) => {
      if (minimumLevel === "grade11" && current.edu_level !== "grade11") {
        return { ...current, edu_level: "grade11" };
      }
      if (!current.edu_level) {
        return { ...current, edu_level: minimumLevel };
      }
      return current;
    });
  }, [form.program_id, programs]);

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => ({ ...e, [field]: "" }));
  };

  const setDoc = (type, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setDocErrors(e => ({ ...e, [type]: "File must be under 15 MB" }));
      return;
    }
    const allowed = [
      "image/jpeg", "image/png", "image/webp", "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];
    if (!allowed.includes(file.type) && !file.name.endsWith('.doc') && !file.name.endsWith('.docx')) {
      setDocErrors(e => ({ ...e, [type]: "Only JPEG, PNG, WebP, PDF, or Word allowed" }));
      return;
    }
    setDocs(d => ({ ...d, [type]: file }));
    setDocErrors(e => ({ ...e, [type]: "" }));
  };

  // ── Validation ───────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.first_name.trim())  e.first_name    = "Required";
      if (!form.last_name.trim())   e.last_name     = "Required";
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
      if (!form.phone.trim())       e.phone         = "Required";
      if (!form.date_of_birth)      e.date_of_birth = "Required";
      if (!form.id_number.trim())   e.id_number     = "Required";
    }
    if (step === 1) {
      if (!form.address_street.trim())   e.address_street   = "Required";
      if (!form.address_city.trim())     e.address_city     = "Required";
      if (!form.address_district.trim()) e.address_district = "Required";
    }
    if (step === 2) {
      if (!form.program_id)          e.program_id   = "Select a program";
      if (!form.intake_year)         e.intake_year  = "Required";
      if (!form.edu_level)           e.edu_level    = "Required";
      if (!form.highest_qual.trim()) e.highest_qual = "Required";
      if (!form.school_name.trim())  e.school_name  = "Required";
    }
    if (step === 3) {
      if (!docs.motivation_letter && form.motivation.trim().length < 50) {
        e.motivation = "Please write at least 50 characters OR upload a motivation letter in Step 6.";
      }
    }
    if (step === 5) {
      // validate required documents
      DOCS_CONFIG.filter(d => d.required).forEach(d => {
        if (!docs[d.type]) e[d.type] = "This document is required";
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  // ── Submit: first create application, then upload docs ───────
  const handleSubmit = async () => {
    setLoading(true);
    setErrors({});
    try {
      // 1. Submit application data
      const res  = await fetch(`${API_BASE}/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Submission failed");

      const appId = data.id;

      // 2. Upload each document
      setDocUploading(true);
      for (const [type, file] of Object.entries(docs)) {
        const fd = new FormData();
        fd.append("doc_type", type);
        fd.append("file", file);
        const docRes = await fetch(`${API_BASE}/applications/${appId}/documents`, {
          method: "POST",
          body: fd,
        });
        if (!docRes.ok) {
          const docData = await docRes.json();
          throw new Error(`Document upload failed (${type}): ${docData.error}`);
        }
      }

      setRefId(appId);
      setSubmitted(true);
    } catch (err) {
      setErrors({ submit: err.message });
    } finally {
      setLoading(false);
      setDocUploading(false);
    }
  };

  // ── Success ──────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="page">
        <div className="apply-success">
          <div className="apply-success__icon">✅</div>
          <h2>Application Submitted!</h2>
          <p>Thank you, <strong>{form.first_name}</strong>. Your application has been received.</p>
          <div className="apply-success__ref">
            Reference #: <strong>APP-{String(refId).padStart(5, "0")}</strong>
          </div>
          <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginTop: 12 }}>
            We will review your application within 5 working days and contact you at <strong>{form.email}</strong>.
          </p>

          <div className="apply-success__docs" style={{ marginTop: 24, textAlign: 'left', width: '100%', maxWidth: 400, marginInline: 'auto' }}>
            <h4 style={{ fontSize: '0.9rem', marginBottom: 12, color: 'var(--text-main)' }}>Uploaded Documents:</h4>
            {DOCS_CONFIG.map(doc => docs[doc.type] && (
              <div key={doc.type} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 6 }}>
                <span>✅</span>
                <span>{doc.label}</span>
              </div>
            ))}
          </div>

          <a href="/" className="btn btn--accent btn--lg" style={{ marginTop: 32 }}>Back to Home</a>
        </div>
      </div>
    );
  }

  const selectedProgram = programs.find(p => String(p.id) === String(form.program_id));

  return (
    <div className="page">
      <header className="page__hero">
        <h1>Online Application</h1>
        <p>Complete all steps to submit your application for the {form.intake_year} intake.</p>
      </header>

      <section className="section">
        <div className="section__inner">

          {/* Stepper */}
          <div className="apply-stepper">
            {STEPS.map((label, i) => (
              <div key={label} className={`apply-stepper__step ${i === step ? "active" : ""} ${i < step ? "done" : ""}`}>
                <div className="apply-stepper__circle">{i < step ? "✓" : (i + 1)}</div>
                <span className="apply-stepper__label">{label}</span>
                {i < STEPS.length - 1 && <div className="apply-stepper__line" />}
              </div>
            ))}
          </div>

          <div className="apply-card">
            {errors.submit && <div className="apply-error">{errors.submit}</div>}
            {apiError && <div className="apply-error">API Error: {apiError}. Please check your internet connection and try again.</div>}

            {/* ── Step 0: Personal Info ── */}
            {step === 0 && (
              <div className="apply-grid">
                <h2 className="apply-card__title">Personal Information</h2>
                <Field label="First Name *" error={errors.first_name}>
                  <input className={`apply-input ${errors.first_name ? "apply-input--err" : ""}`}
                    value={form.first_name} onChange={e => set("first_name", e.target.value)} placeholder="e.g. Thabo" />
                </Field>
                <Field label="Last Name *" error={errors.last_name}>
                  <input className={`apply-input ${errors.last_name ? "apply-input--err" : ""}`}
                    value={form.last_name} onChange={e => set("last_name", e.target.value)} placeholder="e.g. Mokoena" />
                </Field>
                <Field label="Email Address *" error={errors.email}>
                  <input type="email" className={`apply-input ${errors.email ? "apply-input--err" : ""}`}
                    value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@example.com" />
                </Field>
                <Field label="Phone Number *" error={errors.phone}>
                  <input className={`apply-input ${errors.phone ? "apply-input--err" : ""}`}
                    value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+266 5X XXX XXX" />
                </Field>
                <Field label="Date of Birth *" error={errors.date_of_birth}>
                  <input type="date" className={`apply-input ${errors.date_of_birth ? "apply-input--err" : ""}`}
                    value={form.date_of_birth} onChange={e => set("date_of_birth", e.target.value)} />
                </Field>
                <Field label="Gender">
                  <select className="apply-input" value={form.gender} onChange={e => set("gender", e.target.value)}>
                    <option value="">Select gender</option>
                    {GENDERS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </Field>
                <Field label="Nationality">
                  <input className="apply-input" value={form.nationality} onChange={e => set("nationality", e.target.value)} />
                </Field>
                <Field label="National ID / Passport Number *" error={errors.id_number}>
                  <input className={`apply-input ${errors.id_number ? "apply-input--err" : ""}`}
                    value={form.id_number} onChange={e => set("id_number", e.target.value)} placeholder="ID or Passport number" />
                </Field>
              </div>
            )}

            {/* ── Step 1: Address ── */}
            {step === 1 && (
              <div className="apply-grid">
                <h2 className="apply-card__title">Address Details</h2>
                <Field label="Street / Village *" error={errors.address_street} full>
                  <input className={`apply-input ${errors.address_street ? "apply-input--err" : ""}`}
                    value={form.address_street} onChange={e => set("address_street", e.target.value)} placeholder="e.g. 12 Main Road, Ha Thetsane" />
                </Field>
                <Field label="City / Town *" error={errors.address_city}>
                  <input className={`apply-input ${errors.address_city ? "apply-input--err" : ""}`}
                    value={form.address_city} onChange={e => set("address_city", e.target.value)} placeholder="e.g. Maseru" />
                </Field>
                <Field label="District *" error={errors.address_district}>
                  <input className={`apply-input ${errors.address_district ? "apply-input--err" : ""}`}
                    value={form.address_district} onChange={e => set("address_district", e.target.value)} placeholder="e.g. Maseru" />
                </Field>
                <Field label="Country">
                  <input className="apply-input" value={form.address_country} onChange={e => set("address_country", e.target.value)} />
                </Field>
              </div>
            )}

            {/* ── Step 2: Academic ── */}
            {step === 2 && (
              <div className="apply-grid">
                <h2 className="apply-card__title">Academic & Program</h2>
                <Field label="Program *" error={errors.program_id} full>
                  <select className={`apply-input ${errors.program_id ? "apply-input--err" : ""}`}
                    value={form.program_id} onChange={e => set("program_id", e.target.value)}>
                    <option value="">Select a program</option>
                    {programs.length > 0 ? (
                      programs.map(p => <option key={p.id} value={p.id}>{p.name} ({p.duration})</option>)
                    ) : apiError ? (
                      <option value="" disabled>Error loading programs</option>
                    ) : (
                      <option value="" disabled>Loading programs...</option>
                    )}
                  </select>
                  {programs.length === 0 && !apiError && (
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Loading available programs...
                    </p>
                  )}
                  {apiError && (
                    <p style={{ fontSize: '0.8rem', color: '#ef4444', marginTop: '4px' }}>
                      Unable to load programs. Please refresh the page.
                    </p>
                  )}
                </Field>
                <Field label="Intake Year *" error={errors.intake_year}>
                  <select className="apply-input" value={form.intake_year} onChange={e => set("intake_year", e.target.value)}>
                    {[0,1,2].map(offset => {
                      const y = new Date().getFullYear() + offset;
                      return <option key={y} value={y}>{y}</option>;
                    })}
                  </select>
                </Field>
                <Field label="Education Level *" error={errors.edu_level}>
                  <select
                    className={`apply-input ${errors.edu_level ? "apply-input--err" : ""}`}
                    value={form.edu_level}
                    onChange={e => set("edu_level", e.target.value)}
                  >
                    <option value="">Select your highest school level</option>
                    <option value="standard7">Standard 7</option>
                    <option value="grade11">Grade 11 / LGCSE / COSC or higher</option>
                  </select>
                  {selectedProgram?.min_edu_level === "grade11" && (
                    <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: 6 }}>
                      This program requires Grade 11 / LGCSE / COSC or higher.
                    </p>
                  )}
                </Field>
                <Field label="Highest Qualification *" error={errors.highest_qual}>
                  <input className={`apply-input ${errors.highest_qual ? "apply-input--err" : ""}`}
                    value={form.highest_qual} onChange={e => set("highest_qual", e.target.value)} placeholder="e.g. COSC, LGCSE" />
                </Field>
                <Field label="School / Institution Name *" error={errors.school_name}>
                  <input className={`apply-input ${errors.school_name ? "apply-input--err" : ""}`}
                    value={form.school_name} onChange={e => set("school_name", e.target.value)} placeholder="e.g. Maseru High School" />
                </Field>
                <Field label="Year Completed">
                  <input type="number" className="apply-input" value={form.school_year}
                    onChange={e => set("school_year", e.target.value)} placeholder="e.g. 2023" min="2000" max="2030" />
                </Field>
              </div>
            )}

            {/* ── Step 3: Motivation ── */}
            {step === 3 && (
              <div>
                <h2 className="apply-card__title">Motivation Letter</h2>
                <p style={{ color: "var(--text-muted)", marginBottom: 16, fontSize: "0.9rem" }}>
                  Tell us why you want to study at Mokorotlo College and what you hope to achieve. (min. 50 characters)
                </p>
                
                {docs.motivation_letter ? (
                  <div className="apply-info-box" style={{ background: "rgba(16, 185, 129, 0.1)", border: "1px solid #10b981", padding: "16px", borderRadius: "8px", marginBottom: "20px" }}>
                    <p style={{ color: "#059669", fontWeight: "600", marginBottom: "4px" }}>✓ Motivation Letter Uploaded</p>
                    <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                      You have uploaded <strong>{docs.motivation_letter.name}</strong>. You can still write additional notes below if you wish, or leave it blank.
                    </p>
                  </div>
                ) : (
                  <p style={{ color: "var(--text-muted)", marginBottom: 16, fontSize: "0.85rem", fontStyle: "italic" }}>
                    Tip: You can also upload a Word or PDF file of your motivation letter in the "Documents" step if you prefer.
                  </p>
                )}

                <Field label={`Motivation ${!docs.motivation_letter ? "*" : "(Optional)"}`} error={errors.motivation} full>
                  <textarea
                    className={`apply-input apply-textarea ${errors.motivation ? "apply-input--err" : ""}`}
                    value={form.motivation}
                    onChange={e => set("motivation", e.target.value)}
                    rows={10}
                    placeholder="Write your motivation here…"
                  />
                </Field>
                <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: 6 }}>
                  {form.motivation.trim().length} / 50 characters minimum
                </p>
              </div>
            )}

            {/* ── Step 4: Payment ── */}
            {step === 4 && (
              <div>
                <h2 className="apply-card__title">Application Fee Payment</h2>
                <div className="payment-info-card">
                  <div className="payment-info-card__badge">Ecocash Payment</div>
                  <p className="payment-info-card__instruction">
                    Before uploading documents, please pay the <strong>R150 application fee</strong> via Ecocash using the details below.
                    Take a <strong>screenshot</strong> of the confirmation message — you will upload it in the next step.
                  </p>
                  <div className="payment-info-card__details">
                    <div className="payment-info-card__row">
                      <span className="payment-info-card__label">Amount</span>
                      <span className="payment-info-card__value">R150</span>
                    </div>
                    <div className="payment-info-card__row">
                      <span className="payment-info-card__label">Send To (Number)</span>
                      <span className="payment-info-card__value">77095</span>
                    </div>
                    <div className="payment-info-card__row">
                      <span className="payment-info-card__label">Account Name</span>
                      <span className="payment-info-card__value">Nyamatsana Visuals</span>
                    </div>
                    <div className="payment-info-card__row">
                      <span className="payment-info-card__label">Reference</span>
                      <span className="payment-info-card__value">{form.first_name} {form.last_name} – Application Fee</span>
                    </div>
                  </div>
                  <div className="payment-info-card__steps">
                    <p>📱 <strong>How to pay:</strong></p>
                    <ol>
                      <li>Open Ecocash on your phone</li>
                      <li>Select <strong>Send Money</strong></li>
                      <li>Enter number: <strong>77095</strong></li>
                      <li>Enter the application fee amount</li>
                      <li>Use your name as reference</li>
                      <li>Take a <strong>screenshot</strong> of the confirmation SMS</li>
                    </ol>
                  </div>
                  <div className="payment-info-card__note">
                    ⚠️ Your application will not be processed without proof of payment.
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 5: Documents ── */}
            {step === 5 && (
              <div>
                <h2 className="apply-card__title">Upload Documents</h2>
                <p style={{ color: "var(--text-muted)", marginBottom: 24, fontSize: "0.9rem" }}>
                  Upload clear scans or photos. Accepted formats: <strong>JPEG, PNG, WebP, PDF</strong>. Max <strong>5 MB</strong> per file.
                </p>
                <div className="docs-grid">
                  {DOCS_CONFIG.map(doc => (
                    <div key={doc.type} className={`doc-upload-card ${docs[doc.type] ? "doc-upload-card--done" : ""} ${errors[doc.type] ? "doc-upload-card--err" : ""}`}>
                      <div className="doc-upload-card__header">
                        <span className="doc-upload-card__icon">
                          {docs[doc.type] ? "✅" : doc.required ? "📄" : "📎"}
                        </span>
                        <div>
                          <p className="doc-upload-card__label">
                            {doc.label}
                            {doc.required && <span className="doc-upload-card__req"> *</span>}
                          </p>
                          <p className="doc-upload-card__hint">{doc.hint}</p>
                        </div>
                      </div>

                      {docs[doc.type] ? (
                        <div className="doc-upload-card__preview">
                          <span className="doc-upload-card__filename">
                            {docs[doc.type].name.length > 35
                              ? docs[doc.type].name.substring(0, 35) + "…"
                              : docs[doc.type].name}
                          </span>
                          <button
                            type="button"
                            className="doc-upload-card__remove"
                            onClick={() => setDocs(d => { const n = { ...d }; delete n[doc.type]; return n; })}
                          >
                            ✕ Remove
                          </button>
                        </div>
                      ) : (
                        <label className="doc-upload-card__btn">
                          Choose File
                          <input
                            type="file"
                            accept={doc.accept}
                            style={{ display: "none" }}
                            onChange={e => setDoc(doc.type, e.target.files[0])}
                          />
                        </label>
                      )}

                      {(errors[doc.type] || docErrors[doc.type]) && (
                        <p className="doc-upload-card__error">{errors[doc.type] || docErrors[doc.type]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Step 6: Review ── */}
            {step === 6 && (
              <div>
                <h2 className="apply-card__title">Review Your Application</h2>
                <p style={{ color: "var(--text-muted)", marginBottom: 20, fontSize: "0.9rem" }}>
                  Please review all details before submitting. Click Back to make changes.
                </p>
                <div className="apply-review">
                  <ReviewSection title="Personal Info" rows={[
                    ["Full Name", `${form.first_name} ${form.last_name}`],
                    ["Email", form.email], ["Phone", form.phone],
                    ["Date of Birth", form.date_of_birth], ["ID Number", form.id_number],
                    ["Nationality", form.nationality],
                  ]} />
                  <ReviewSection title="Address" rows={[
                    ["Street", form.address_street], ["City", form.address_city],
                    ["District", form.address_district], ["Country", form.address_country],
                  ]} />
                  <ReviewSection title="Academic" rows={[
                    ["Program", selectedProgram?.name || form.program_id],
                    ["Intake Year", form.intake_year],
                    ["Education Level", form.edu_level === "grade11" ? "Grade 11 / LGCSE / COSC or higher" : form.edu_level === "standard7" ? "Standard 7" : ""],
                    ["Qualification", form.highest_qual],
                    ["School", form.school_name],
                    ["Year Completed", form.school_year],
                  ]} />
                  <div className="apply-review__section">
                    <h4>Motivation</h4>
                    <p style={{ color: "var(--text-muted)", fontSize: "0.88rem", lineHeight: 1.7 }}>{form.motivation}</p>
                  </div>
                  <div className="apply-review__section">
                    <h4>Documents</h4>
                    <div className="apply-review__docs">
                      {DOCS_CONFIG.map(doc => (
                        <div key={doc.type} className="apply-review__doc-row">
                          <span>{docs[doc.type] ? "✅" : "—"}</span>
                          <span>{doc.label}</span>
                          <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>
                            {docs[doc.type] ? docs[doc.type].name : (doc.required ? "Missing" : "Not provided")}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Navigation ── */}
            <div className="apply-nav">
              {step > 0 && (
                <button className="btn btn--outline" onClick={back} disabled={loading}>← Back</button>
              )}
              <div style={{ flex: 1 }} />
              {step < STEPS.length - 1 ? (
                <button className="btn btn--accent" onClick={next}>Next →</button>
              ) : (
                <button className="btn btn--accent btn--lg" onClick={handleSubmit} disabled={loading}>
                  {loading
                    ? (docUploading ? "Uploading documents…" : "Submitting…")
                    : "Submit Application ✓"}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, error, full, children }) {
  return (
    <div className={`apply-field ${full ? "apply-field--full" : ""}`}>
      <label className="apply-label">{label}</label>
      {children}
      {error && <span className="apply-field__error">{error}</span>}
    </div>
  );
}

function ReviewSection({ title, rows }) {
  return (
    <div className="apply-review__section">
      <h4>{title}</h4>
      <dl className="apply-review__dl">
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="apply-review__row">
            <dt>{k}</dt><dd>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
